import { CREDIT_RATES } from '@lyrmo/shared/credits';
import { MAX_DICTATION_BYTES, MAX_DICTATION_SECONDS } from '@lyrmo/shared/dictation';
import type { ApiClient } from '../../api/client';
import { ApiError } from '@lyrmo/client/api-error';
export interface RecordedDictationOptions {
  readonly transcribe: ApiClient['transcribeAudio'];
  /** Stops the recording by itself after this many seconds; three minutes at most. */
  readonly maxSeconds?: number | undefined;
  readonly onListening: (stream: MediaStream) => void;
  /** `limit` when the recording stopped by itself, at `maxSeconds` or three minutes. */
  readonly onFinishing: (ended: 'stopped' | 'limit') => void;
  readonly onComplete: (text: string) => void;
  readonly onError: (error: unknown) => void;
}

const MEDIA_TYPES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm'] as const;

/**
 * How long a recording the credits left pay for, stopping a second short so the audio's last
 * moments don't round up past them: three minutes at most, and 0 when they don't pay for any.
 * Unknown credits leave it at three minutes, as the server transcribes only what they pay for.
 */
export function dictationLimit(creditsLeft: number | undefined): number {
  if (creditsLeft === undefined) return MAX_DICTATION_SECONDS;
  const seconds = Math.floor(creditsLeft / CREDIT_RATES.transcriptionPerSecond) - 1;
  return Math.max(0, Math.min(MAX_DICTATION_SECONDS, seconds));
}

export function recordingMediaType(): string | undefined {
  return typeof MediaRecorder === 'undefined'
    ? undefined
    : MEDIA_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

/** Keep audio in memory until Stop, then send one file transcription request. */
export function createRecordedDictation(options: RecordedDictationOptions) {
  const controller = new AbortController();
  let stream: MediaStream | undefined;
  let recorder: MediaRecorder | undefined;
  let chunks: Blob[] = [];
  let bytes = 0;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  let closed = false;
  let finishing = false;

  function stopMicrophone() {
    stream?.getTracks().forEach((track) => {
      track.stop();
    });
    stream = undefined;
  }

  function cancel() {
    if (closed) return;
    closed = true;
    clearTimeout(deadline);
    controller.abort();
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        // A device failure can stop the recorder before its state has caught up.
      }
    }
    stopMicrophone();
    chunks = [];
    recorder = undefined;
  }

  function fail(error: unknown) {
    if (closed) return;
    cancel();
    options.onError(error);
  }

  async function upload() {
    if (closed) return;
    try {
      if (!finishing || !bytes) throw new Error('The recording ended without audio.');
      const audio = new Blob(chunks, { type: recorder?.mimeType });
      chunks = [];
      clearTimeout(deadline);
      deadline = setTimeout(() => {
        fail(new ApiError('UPSTREAM_TIMEOUT', 'Transcription took too long. Please try again.'));
      }, 120_000);
      const text = await options.transcribe(audio, { signal: controller.signal });
      if (controller.signal.aborted) return;
      cancel();
      options.onComplete(text);
    } catch (error) {
      fail(error);
    }
  }

  function end(ended: 'stopped' | 'limit') {
    if (closed || finishing || recorder?.state !== 'recording') return;
    finishing = true;
    clearTimeout(deadline);
    deadline = setTimeout(() => {
      fail(new Error('The recording did not finish.'));
    }, 10_000);
    options.onFinishing(ended);
    try {
      recorder.stop();
      stopMicrophone();
    } catch (error) {
      fail(error);
    }
  }

  async function start() {
    try {
      const mimeType = recordingMediaType();
      if (!mimeType) throw new Error('Audio recording is not supported here.');
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (closed) {
        stopMicrophone();
        return;
      }
      recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 64_000 });
      recorder.ondataavailable = ({ data }) => {
        if (closed || !data.size) return;
        bytes += data.size;
        if (bytes > MAX_DICTATION_BYTES) {
          fail(
            new ApiError(
              'PAYLOAD_TOO_LARGE',
              'The recording is too large. Please record it again.',
            ),
          );
          return;
        }
        chunks.push(data);
      };
      recorder.onstop = () => {
        void upload();
      };
      recorder.onerror = () => {
        fail(new Error('Audio recording was interrupted.'));
      };
      recorder.start(1_000);
      const seconds = Math.min(MAX_DICTATION_SECONDS, options.maxSeconds ?? MAX_DICTATION_SECONDS);
      deadline = setTimeout(() => {
        end('limit');
      }, seconds * 1_000);
      options.onListening(stream);
    } catch (error) {
      fail(error);
    }
  }

  return {
    start,
    stop: () => {
      end('stopped');
    },
    cancel,
  };
}
